/**
 * Finnish-aware phone comparison for person duplicate warnings (#2904).
 *
 * Strips spaces and dashes. Treats +358 / 358 and a leading 0 as the same
 * national number so "040 123 4567" matches "+358401234567".
 */

const SEPARATORS = /[\s-]/g;

export function stripPhoneSeparators(phone: string): string {
  return phone.trim().replace(SEPARATORS, '');
}

function toDigitKey(phone: string): string {
  return stripPhoneSeparators(phone).replace(/^\+/, '').replace(/\D/g, '');
}

/** Digit keys that should match a stored Finnish / E.164 number. */
export function phoneMatchKeys(phone: string): string[] {
  const digits = toDigitKey(phone);
  if (!digits) return [];
  const keys = new Set<string>([digits]);
  if (digits.startsWith('358') && digits.length > 3) {
    keys.add(`0${digits.slice(3)}`);
  }
  if (digits.startsWith('0') && digits.length > 1) {
    keys.add(`358${digits.slice(1)}`);
  }
  return [...keys];
}

export function phonesMatch(
  a: string | null | undefined,
  b: string | null | undefined,
): boolean {
  if (!a?.trim() || !b?.trim()) return false;
  const other = new Set(phoneMatchKeys(b));
  return phoneMatchKeys(a).some((key) => other.has(key));
}

/**
 * `q` for GET /persons that is likely to hit either stored format.
 * Prefers the national 0-prefix form Finnish workshops usually type.
 */
export function phoneListSearchQuery(phone: string): string {
  const keys = phoneMatchKeys(phone);
  const national = keys.find((key) => key.startsWith('0'));
  return national ?? keys[0] ?? stripPhoneSeparators(phone);
}
